// Normalizing an incoming URL into a typed, allowlisted deep-link action
// (#1693, spec.md §6.1-6.2).
//
// A URL reaches here from a Universal Link/App Link tap (appUrlOpen /
// getLaunchUrl — see nativeLinkListener.js) or from the app's own custom
// scheme fallback. It is never handed to the WebView or a router as-is:
// this validates the origin and the path against the allowlist first, and
// returns `null` for anything it does not recognize.

import { getPublicAppOrigin } from '../platform/publicAppOrigin'
import { isAllowedDeepLinkPath } from './allowedRoutes'

// The custom scheme is an INTERNAL fallback only (spec §6.1) — it must
// never be written to an NFC tag or embedded in a QR code; those always
// carry the canonical https:// PUBLIC_APP_ORIGIN link (see stationLink() in
// src/services/stations.js). It exists so a native context that for some
// reason cannot resolve the Universal Link/App Link (e.g. the association
// files aren't verified yet on a given device) still has a path in.
export const DEEP_LINK_CUSTOM_SCHEME = 'kravcalc://'

/**
 * @param {string} rawUrl e.g. "https://kravetzky.com/attendance?report=1"
 *   or "kravcalc://attendance?report=1".
 * @return {{type: 'deepLink', path: string, search: string, createdAt: number}|null}
 *   `null` when the URL's origin is unrecognized OR its path is not on the
 *   allowlist — both cases are refused identically, on purpose: a caller
 *   must not be able to tell "wrong domain" from "right domain, unknown
 *   route" from the return value alone.
 */
export function normalizeDeepLink(rawUrl) {
  if (typeof rawUrl !== 'string' || !rawUrl) return null

  const isCustomSchemeFallback = rawUrl.startsWith(DEEP_LINK_CUSTOM_SCHEME)

  let url
  try {
    url = new URL(rawUrl)
  } catch {
    return null
  }

  const publicOrigin = getPublicAppOrigin()
  const isHttpsFromKnownOrigin = Boolean(publicOrigin) && url.origin === publicOrigin

  if (!isHttpsFromKnownOrigin && !isCustomSchemeFallback) {
    return null
  }

  // kravcalc://attendance?report=1 parses with hostname "attendance" and
  // pathname "" (or "/") — fold the two into the same "/attendance" shape
  // an https:// link would carry, so the allowlist only needs one form.
  const pathname = isCustomSchemeFallback
    ? `/${url.hostname}${url.pathname === '/' ? '' : url.pathname}`
    : url.pathname

  if (!isAllowedDeepLinkPath(pathname)) {
    return null
  }

  return {
    type: 'deepLink',
    path: pathname,
    search: url.search || '',
    createdAt: Date.now(),
  }
}
